import { useEffect, useState } from "react";
import api from "@/lib/api";
import safeStorage from "@/lib/safeStorage";
import StaffLayout from "@/components/StaffLayout";
import { toast } from "sonner";
import { Save, Star, ExternalLink, QrCode, Globe } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SUPPORTED_LANGUAGES } from "@/lib/i18n";

export default function AdminSettings() {
  const { t, setLang, refresh: refreshLang } = useLang();
  const [name, setName] = useState("");
  const [reviewUrl, setReviewUrl] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [guestUrl, setGuestUrl] = useState("");
  const [language, setLanguage] = useState("en");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings");
      setName(data.restaurant_name || "");
      setReviewUrl(data.google_review_url || "");
      setPlaceId(data.google_place_id || "");
      setGuestUrl(data.guest_url || "");
      setLanguage(data.language || "en");
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", {
        restaurant_name: name,
        google_review_url: reviewUrl,
        google_place_id: placeId,
        guest_url: guestUrl,
        language,
      });
      // Apply the new language across the whole staff UI immediately,
      // then re-sync with the server as a safety net.
      setLang(language);
      safeStorage.setItem("lang", language);
      toast.success(t("settings_saved"));
      refreshLang();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StaffLayout>
      <div className="overline text-zinc-500">{t("admin")}</div>
      <h1 className="font-condensed text-5xl font-bold tracking-tight mt-1">{t("settings_title")}</h1>

      <form onSubmit={save} className="mt-10 max-w-2xl space-y-6" data-testid="settings-form">
        <div>
          <label className="overline text-zinc-500 flex items-center gap-2">
            <Globe size={12} className="text-[#c85a40]" /> {t("language")}
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full mt-2 px-3 py-2.5 font-plex text-sm"
            data-testid="settings-language"
          >
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
          <p className="font-plex text-xs text-zinc-500 mt-2">
            {t("settings_language_hint")}
          </p>
        </div>

        <div>
          <label className="overline text-zinc-500">{t("restaurant_name_lbl")}</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full mt-2 px-3 py-2.5 font-plex text-sm"
            data-testid="settings-name"
          />
        </div>

        <div>
          <label className="overline text-zinc-500 flex items-center gap-2">
            <QrCode size={12} className="text-[#c85a40]" /> {t("guest_domain")}
          </label>
          <input
            type="url"
            value={guestUrl}
            onChange={(e) => setGuestUrl(e.target.value)}
            className="w-full mt-2 px-3 py-2.5 font-plex text-sm"
            data-testid="settings-guest-url"
            placeholder="https://clients.taptable.com"
          />
        </div>

        <div>
          <label className="overline text-zinc-500 flex items-center gap-2">
            <Star size={12} className="text-[#c85a40]" /> {t("google_place_id_lbl")}
          </label>
          <input
            value={placeId}
            onChange={(e) => setPlaceId(e.target.value)}
            className="w-full mt-2 px-3 py-2.5 font-plex text-sm"
            data-testid="settings-place-id"
            placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
          />
          <p className="font-plex text-xs text-zinc-500 mt-2">
            Your restaurant&apos;s Google Place ID. Find it at{" "}
            <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noreferrer" className="text-[#007aff] hover:underline">developers.google.com/place-id</a>.
            When set (and no review link above), the customer review button auto-links to your Google review page.
          </p>
        </div>

        <div>
          <label className="overline text-zinc-500 flex items-center gap-2">
            <Star size={12} className="text-[#c85a40]" /> {t("google_review_link")}
          </label>
          <input
            type="url"
            value={reviewUrl}
            onChange={(e) => setReviewUrl(e.target.value)}
            className="w-full mt-2 px-3 py-2.5 font-plex text-sm"
            data-testid="settings-review-url"
            placeholder="https://g.page/r/…/review"
          />
          {reviewUrl ? (
            <a href={reviewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-3 text-xs text-[#007aff] font-plex hover:underline">
              <ExternalLink size={12} /> Preview link
            </a>
          ) : null}
        </div>

        <button
          type="submit"
          disabled={saving}
          data-testid="save-settings-btn"
          className="bg-white text-black px-5 py-3 font-plex text-sm hover:bg-[#007aff] hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Save size={14} /> {saving ? t("saving") : t("save_settings")}
        </button>
      </form>
    </StaffLayout>
  );
}
